import React, { useEffect, useState } from 'react';
import BackButton from '../components/ui/BackButton';
import Accordion from '../components/ui/Accordion';
import Checkbox from '../components/ui/Checkbox';
import Button from '../components/ui/Button';
import { useTelegram } from '../contexts/TelegramContext';
import ArrowsLine from '../assets/icons/ArrowsLine';
import { Link, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '../components/ui/Form';
import MultiSelector from '../components/ui/MultiSelector';
import { Option, Preferences } from '../types';
import { zodResolver } from '@hookform/resolvers/zod';
import Input from '../components/ui/Input';
import {
  getCompanySizes,
  getCountries,
  getIndustries,
  getPreferences,
  updatePreferences,
} from '../api/user-preferences';
import { PreferencesFormValues, PreferencesSchema } from '../validation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteUser } from '../api/user';

const PrivacyLabel = () => (
  <div
    className={
      'w-full text-left text-sm font-medium text-green-blue-2 transition-colors'
    }
  >
    I have read and agree to the
    <Link
      to={'https://robocorp.co/terms-conditions'}
      className={`
        mx-1 bg-green-gradient bg-clip-text text-transparent underline decoration-neon-green
      `}
    >
      Terms of Use
    </Link>
    and the

    <Link
      to={'https://robocorp.co/privacy-policy'}
      className={`
        ml-1 bg-green-gradient bg-clip-text text-transparent underline decoration-neon-green
      `}
    >
      Privacy Policy
    </Link>
    .
  </div>
);

const ConfigPage = () => {
  const { tgApp } = useTelegram();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: (values: Preferences) => updatePreferences(values) ,
    onError: (error) => alert(`Error: ${error.message}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      navigate(-1);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onError: (error) => alert(`Error: ${error.message}`),
    onSuccess: () => tgApp && tgApp.close(),
  });

  const logout = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete all your data? This action cannot be undone.',
      )
    )
      return;

    deleteMutation.mutate();
  };

  const [countries, setCountries] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [companySizes, setCompanySizes] = useState<string[]>([]);

  const form = useForm<PreferencesFormValues>({
    resolver: zodResolver(PreferencesSchema),
    defaultValues: {
      industriesToExclude: [],
      countriesToExclude: [],
      companySizesToExclude: [],
      minOfferDaac: 0.01,
      minOfferDurationMonths: 1,
      consentDataCollection: false,
      consentRevocableAck: false,
      consentPolicyAck: false,
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [preferences, sizes, industries, countries] = await Promise.all([
          getPreferences(),
          getCompanySizes(),
          getIndustries(),
          getCountries(),
        ]);

        const minOfferDaac =
          preferences.minOfferDaac >= 0.01 ? preferences.minOfferDaac : 0.01;
        const minOfferDurationMonths =
          preferences.minOfferDurationMonths >= 1
            ? preferences.minOfferDurationMonths
            : 1;

        form.reset({ ...preferences, minOfferDaac, minOfferDurationMonths });

        setCompanySizes(sizes);
        setIndustries(industries);
        setCountries(countries);
      } catch (error) {}
    };

    fetchData();
  }, []);

  return (
    <div
      className={`
        from-gray-950 via-gray-900 to-gray-950 flex min-h-screen flex-col bg-gradient-to-br
      `}
    >
      <Form {...form}>
        <form className='mx-auto flex w-full max-w-2xl flex-1 flex-col gap-6 p-4'>
          <div className='flex flex-col gap-6'>
            <div className='flex flex-row gap-2 border-b border-neon-green pb-2'>
              <BackButton />

              <div className='text-sm font-semibold text-green-blue-0'>
                <h2 className='text-white'>Controls</h2>

                <div className='text-green-blue-2'>
                  Here you can configure your preferences
                </div>
              </div>
            </div>

            <div className='flex flex-col gap-2'>
              <Accordion
                title="Who can't access my data?"
                defaultOpen={
                  !!form.formState.errors.industriesToExclude ||
                  !!form.formState.errors.countriesToExclude ||
                  !!form.formState.errors.companySizesToExclude
                }
              >
                <div className='flex flex-col gap-2 pt-4'>
                  <FormField
                    control={form.control}
                    name='industriesToExclude'
                    render={({ field }) => (
                      <FormItem className='flex flex-col gap-1'>
                        <FormControl>
                          <MultiSelector
                            value={field.value.map((value) => ({
                              value: value,
                              label: value,
                            }))}
                            setValue={(val: Option[]) =>
                              field.onChange(val.map((v) => v.value))
                            }
                            label='Industries'
                            placeholder='Industries'
                            options={industries.map((industry) => ({
                              value: industry,
                              label: industry,
                            }))}
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='countriesToExclude'
                    render={({ field }) => (
                      <FormItem className='flex flex-col gap-1'>
                        <FormControl>
                          <MultiSelector
                            value={field.value.map((value) => ({
                              value: value,
                              label: value,
                            }))}
                            setValue={(val: Option[]) =>
                              field.onChange(val.map((v) => v.value))
                            }
                            label='Countries'
                            placeholder='Countries'
                            options={countries.map((country) => ({
                              value: country,
                              label: country,
                            }))}
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='companySizesToExclude'
                    render={({ field }) => (
                      <FormItem className='flex flex-col gap-1'>
                        <FormControl>
                          <MultiSelector
                            value={field.value.map((value) => ({
                              value: value,
                              label: value,
                            }))}
                            setValue={(val: Option[]) =>
                              field.onChange(val.map((v) => v.value))
                            }
                            label='Company sizes'
                            placeholder='Company sizes'
                            options={companySizes.map((size) => ({
                              value: size,
                              label: size,
                            }))}
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Accordion>

              <Accordion
                title='Thresholds'
                defaultOpen={
                  !!form.formState.errors.minOfferDaac ||
                  !!form.formState.errors.minOfferDurationMonths
                }
              >
                <div className='flex flex-col gap-2 pt-4'>
                  <FormField
                    control={form.control}
                    name='minOfferDaac'
                    render={({ field }) => (
                      <FormItem className='flex flex-col gap-1'>
                        <FormControl>
                          <Input
                            {...field}
                            type='number'
                            label='Decline offers lower than (DAAC)'
                            placeholder='Decline offers lower than __ DAAC'
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='minOfferDurationMonths'
                    render={({ field }) => (
                      <FormItem className='flex flex-col gap-1'>
                        <FormControl>
                          <Input
                            {...field}
                            type='number'
                            label='Decline offers shorter than (months)'
                            placeholder='Decline offers shorter than __ months'
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Accordion>

              <Accordion
                title='Consent'
                defaultOpen={
                  !!form.formState.errors.consentDataCollection ||
                  !!form.formState.errors.consentRevocableAck ||
                  !!form.formState.errors.consentPolicyAck
                }
              >
                <div className='flex flex-col gap-2 pt-4'>
                  <FormField
                    control={form.control}
                    name='consentDataCollection'
                    render={({ field }) => (
                      <FormItem className='flex flex-col gap-1'>
                        <FormControl>
                          <Checkbox
                            label='I consent to the collection and processing of my data from the connected platforms '
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='consentRevocableAck'
                    render={({ field }) => (
                      <FormItem className='flex flex-col gap-1'>
                        <FormControl>
                          <Checkbox
                            label='I understand that I can revoke this consent at any time'
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='consentPolicyAck'
                    render={({ field }) => (
                      <FormItem className='flex flex-col gap-1'>
                        <FormControl>
                          <Checkbox
                            label={<PrivacyLabel />}
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Accordion>

              <Accordion title='Unconnection controls '>
                <div className='flex flex-col gap-4 pt-4'>
                  <Button onClick={logout} variant='outlinedPink' type='button'>
                    Delete my account
                  </Button>
                </div>
              </Accordion>
            </div>
          </div>

          <div className='mt-auto flex flex-col gap-4'>
            <Button
              type='button'
              onClick={form.handleSubmit(saveMutation.mutate)}
              loading={form.formState.isSubmitting}
              variant='solid'
              iconBack={<ArrowsLine color='currentColor' />}
            >
              Save Preferences
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ConfigPage;
